import { useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import type { RootState } from '../../store';

/**
 * Detects deep links of the form /diagram?diagramId=<uuid>
 * and enforces authentication before allowing access.
 *
 * Behaviour:
 * - If the current URL has a diagramId query param:
 *   - If the user is NOT authenticated:
 *     - Store the full target URL in sessionStorage under `postLoginRedirect`.
 *     - Redirect the user to the login route.
 *   - If the user IS authenticated and is not already on `/diagram`:
 *     - Redirect to `/diagram?diagramId=...`.
 *
 * Usage:
 * - Call this hook near the top of your app shell (e.g. in AppLayout)
 *   so it runs once on load and when auth state or location changes.
 */
export const useDiagramAuthRedirect = (): void => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const isAuthenticated = useSelector(
    (state: RootState) => state.user?.isLoggedIn ?? state.auth?.isAuthenticated ?? false
  );

  useEffect(() => {
    const diagramId = searchParams.get('diagramId');
    if (!diagramId) {
      return;
    }

    const targetUrl = `${location.pathname}${location.search}`;

    if (!isAuthenticated) {
      // Persist intended target so login can redirect back afterwards.
      try {
        sessionStorage.setItem('postLoginRedirect', targetUrl);
      } catch {
        // ignore storage errors
      }

      // Adjust this path to match your actual login route.
      navigate('/login', { replace: true });
      return;
    }

    // If authenticated but not yet on the /diagram route, redirect there
    // and preserve full query string (diagramId and processId).
    if (location.pathname !== '/diagram' && location.search) {
      navigate(`/diagram${location.search}`, {
        replace: true,
      });
    }
  }, [isAuthenticated, location.pathname, location.search, navigate, searchParams]);
};

